/**
 * Help retention on a registrar course record (#1413). Each learner's Help
 * conversation is kept until its date, then purged by the retention job unless
 * a review hold suspends that. The registrar extends a date (later only), or
 * places or releases a hold, each with a reason, one row at a time. The
 * register names the learner and dates only; it never reads a message.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { formatRetentionDate } from "@/components/help/help-address";
import { useTabDrafts, type TabDraft } from "@/components/ui/box-draft";
import { Bento } from "@/components/ui/box";
import type { Blocker, ListBox, Read, Result, RowTask } from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import { checkCommit, reasonOnError } from "@/lib/api/commitSeams";
import {
  HELP_RETENTION_ACTION,
  helpRetentionApi,
  type RetentionAction,
  type RetentionEvent,
  type RetentionRegister,
  type RetentionThread,
} from "@/lib/api/helpRetention";

type Choice = { action: RetentionAction; deadline: string; reason: string };
type Sent = { key: string; thread: string; effect: string };

const helpRetentionCopy = {
  label: "Help retention",
  open: "Review Help retention",
  job: "Read when each learner's Help conversation is purged, extend that date, or hold a conversation for a review.",
  empty: "No learner has asked for Help on this course.",
  purgedSub: (count: number) => `${count} ${count === 1 ? "conversation was" : "conversations were"} purged after retention.`,
  readFailed: "Help retention could not be read.",
  stale: "Couldn't refresh. A later change isn't shown.",
  held: (count: number) => (count ? `${count} held` : "None held"),
  detail: (count: number, held: number) => `${count} ${count === 1 ? "conversation" : "conversations"} · ${held} held`,
  learner: "Learner",
  keptUntil: (date: string) => `Kept until ${date}`,
  heldFor: (reason: string) => `Held: ${reason}`,
  heldWord: "Held for review",
  change: "Change retention",
  part: "Retention",
  notChanged: "Not changed",
  choose: "Change",
  extend: "Extend the date",
  hold: "Hold for review",
  release: "Release the hold",
  keepUntil: "Keep until",
  reason: "Reason",
  cap: "When you change retention",
  extendEffect: (name: string, date: string) => `Keeps ${name}'s Help conversation until ${date}, then the retention job purges it unless it is held.`,
  holdEffect: (name: string) => `Holds ${name}'s Help conversation: the retention job will not purge it until the hold is released.`,
  releaseEffect: (name: string, date: string) => `Releases the hold on ${name}'s Help conversation. It is purged after ${date}, or at the next run if that date has passed.`,
  recorded: "Recorded with your name and reason in the retention history.",
  needReason: "Give the reason.",
  needLater: (date: string) => `Choose a date later than ${date}.`,
  history: {
    extended: (event: RetentionEvent) => `Extended from ${formatRetentionDate(event.from_deadline)} to ${formatRetentionDate(event.to_deadline ?? event.from_deadline)}`,
    held: () => "Held",
    released: () => "Hold released",
    purged: () => "Purged",
  },
  notConfirmed: "Not confirmed",
  unknown: "No confirmation was received. Your choice is kept. Check this same request before changing it again.",
  check: "Check",
  checkNote: "Reads the saved receipt without sending the change again.",
  notRecorded: "Not recorded",
  noCommit: "The server has no committed change for this request. Review the kept choice before trying again.",
  notChangedCap: "Not changed",
} as const;

const words = helpRetentionCopy;
const imam = { system: "imam" } as const;

function registerRead(query: ReturnType<typeof useRegister>): Read<RetentionRegister> {
  if (query.error instanceof ApiError && [401, 403].includes(query.error.status)) return { status: "forbidden" };
  const readAt = new Date(query.dataUpdatedAt).toISOString();
  if (query.data) {
    if (query.isError) return { status: "stale", last: query.data, readAt, sentence: words.stale, refresh: () => void query.refetch() };
    if (!query.data.threads.length) return { status: "empty", sentence: words.empty, ...(query.data.purged ? { sub: words.purgedSub(query.data.purged) } : {}) };
    return { status: "ready", value: query.data, readAt };
  }
  return query.isError ? { status: "failed", sentence: words.readFailed, retry: () => void query.refetch() } : { status: "loading" };
}

function useRegister(courseId: string) {
  const { me } = useAuth();
  return useQuery({
    queryKey: ["help-retention", me?.tenant_id, me?.user_id, courseId],
    queryFn: () => helpRetentionApi.read(courseId),
    retry: false,
  });
}

const nameOf = (row: RetentionThread) => row.learner_name ?? words.learner;
const nextDay = (iso: string) => {
  const day = new Date(`${iso}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() + 1);
  return day.toISOString().slice(0, 10);
};

function historyLine(event: RetentionEvent): string {
  const what = words.history[event.action](event);
  const who = event.actor_kind === "retention_job" ? "retention job" : event.actor_name;
  const when = new Date(event.recorded_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  return [what, who, when].filter(Boolean).join(" · ") + (event.reason ? `: ${event.reason}` : "");
}

export function HelpRetentionTile({ courseId }: { courseId: string }) {
  const client = useQueryClient();
  const query = useRegister(courseId);
  const read = registerRead(query);
  const drafts = useTabDrafts<Choice, Sent>(`help-retention-${courseId}`);
  const [results, setResults] = useState<Record<string, Result>>({});
  const hold = (thread: string, result: Result) => { setResults(was => ({ ...was, [thread]: result })); return result; };
  const refresh = () => client.invalidateQueries({ queryKey: ["help-retention"] });

  const confirmed = (sent: Sent): Result => ({ state: "confirmed", at: new Date().toISOString(), effects: [sent.effect], remains: [words.recorded] });
  const unknown = (draft: TabDraft<Choice, Sent>, sent: Sent): Result => ({
    state: "unknown", cap: words.notConfirmed, requestId: sent.key, sentence: words.unknown,
    check: { verb: words.check, note: words.checkNote, run: async () => {
      try {
        const answer = await checkCommit(sent.key);
        if (answer.state === "not_committed") {
          draft.setPending(null);
          return hold(sent.thread, { state: "refused", cap: words.notRecorded, sentence: words.noCommit, draft: "kept" });
        }
        if (answer.state === "confirmed" && answer.receipt.action === HELP_RETENTION_ACTION
          && answer.receipt.subject_ids.join("/") === `${courseId}/${sent.thread}`) {
          draft.end();
          await refresh();
          return hold(sent.thread, confirmed(sent));
        }
      } catch { /* An unreadable receipt proves no outcome. */ }
      return hold(sent.thread, unknown(draft, sent));
    } },
  });

  const task = (row: RetentionThread): RowTask => {
    const thread = row.thread_id;
    const draft = drafts({ record: thread, version: `${row.retention_deadline}/${row.held}`, initial: { action: "extend", deadline: "", reason: "" } });
    const name = nameOf(row);
    const current = formatRetentionDate(row.retention_deadline);
    const action: RetentionAction = draft.value.action === "hold" && row.held ? "release" : draft.value.action === "release" && !row.held ? "hold" : draft.value.action;
    const effect = action === "extend"
      ? words.extendEffect(name, draft.value.deadline ? formatRetentionDate(draft.value.deadline) : "the date you choose")
      : action === "hold" ? words.holdEffect(name) : words.releaseEffect(name, current);
    const partId = `retention-${thread}`;
    const busy = !!draft.pending;
    const blockers = (): Blocker[] => [
      ...(action === "extend" && !(draft.value.deadline > row.retention_deadline)
        ? [{ kind: "missing" as const, part: partId, sentence: words.needLater(current) }] : []),
      ...(!draft.value.reason.trim() ? [{ kind: "missing" as const, part: partId, sentence: words.needReason }] : []),
    ];
    return {
      parts: [{
        id: partId, label: words.part, owner: imam, state: { class: "draft", word: words.notChanged },
        fold: "never", draft: "browser", keep: draft,
        render: () => <>
          <label>{words.choose}
            <select className="w-full min-w-0 max-w-full" aria-label={`${words.choose} ${name}`} disabled={busy} value={action}
              onChange={event => draft.set(was => ({ ...was, action: event.target.value as RetentionAction }))}>
              <option value="extend">{words.extend}</option>
              {row.held ? <option value="release">{words.release}</option> : <option value="hold">{words.hold}</option>}
            </select>
          </label>
          {action === "extend" && <label>{words.keepUntil}
            <Input type="date" aria-label={`${words.keepUntil} ${name}`} min={nextDay(row.retention_deadline)} disabled={busy}
              value={draft.value.deadline} onChange={event => draft.set(was => ({ ...was, deadline: event.target.value }))} />
          </label>}
          <label>{words.reason}
            <Input aria-label={`${words.reason} ${name}`} maxLength={1000} disabled={busy} value={draft.value.reason}
              onChange={event => draft.set(was => ({ ...was, reason: event.target.value }))} />
          </label>
        </>,
      }],
      result: draft.pending ? unknown(draft, draft.pending) : results[thread] ?? { state: "none" },
      commit: {
        verb: action === "extend" ? words.extend : action === "hold" ? words.hold : words.release,
        cap: words.cap,
        effects: () => [{ id: "retention", sentence: effect }],
        blockers,
        run: async key => {
          if (draft.pending) return hold(thread, unknown(draft, draft.pending));
          const sent: Sent = { key, thread, effect };
          draft.setPending(sent);
          const end = draft.sending();
          try {
            await helpRetentionApi.change(courseId, thread, {
              action, reason: draft.value.reason.trim(), ...(action === "extend" ? { deadline: draft.value.deadline } : {}),
            }, key);
            draft.setPending(null);
            end();
            await refresh();
            return hold(thread, confirmed(sent));
          } catch (error) {
            if (error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status)) {
              draft.setPending(null);
              if (error.status === 409) void refresh();
              return hold(thread, { state: "refused", cap: words.notChangedCap, sentence: reasonOnError(error)?.message ?? error.message, draft: "kept" });
            }
            return hold(thread, unknown(draft, sent));
          }
        },
      },
    };
  };

  const list: ListBox<RetentionRegister, RetentionThread> = {
    id: `help-retention-${courseId}`, kind: "list", job: words.job, label: words.label, owner: imam, read, fold: "choice",
    act: { does: "open", verb: words.open },
    state: value => ({ class: "quiet", word: words.held(value.threads.filter(row => row.held).length) }),
    detail: value => words.detail(value.threads.length, value.threads.filter(row => row.held).length),
    ...(query.data?.purged ? { sub: (value: RetentionRegister) => words.purgedSub(value.purged) } : {}),
    rows: value => value.threads,
    row: {
      key: row => row.thread_id,
      title: nameOf,
      state: row => (row.held ? { class: "waiting", word: words.heldWord } : null),
      facts: row => [row.sid, words.keptUntil(formatRetentionDate(row.retention_deadline)), row.held && row.hold_reason ? words.heldFor(row.hold_reason) : null]
        .filter(Boolean).join(" · "),
      detail: row => (row.history.length
        ? <ul className="grid gap-1 text-ui text-muted-foreground">{row.history.map(event => <li key={`${event.recorded_at}-${event.action}`}>{historyLine(event)}</li>)}</ul>
        : undefined),
      act: () => ({ does: "open", verb: words.change }),
      task,
    },
  };
  return <Bento id={`help-retention-${courseId}`} journey="registrar-course-record" boxes={[list]} />;
}
